import { ColumnTypes, ITableColumn, RowValue } from '@makyoapp/makyo-sdk/models/Table'
import { ALL_FUNCTIONS_MAP, FunctionItemDefaultValue } from '@makyoapp/makyo-common'

import { Cell, ICell } from '.'
import { ArrayCell } from './array'
import { BooleanCell } from './boolean'
import { DateTimeCell } from './dateTime'
import { ImageCell } from './image'
import { NumberCell } from './number'
import { TextCell } from './text'
import { IconCell } from './icon'

interface FunctionSettingsItem {
  id: string
  fn: string
  title: string
}

interface FunctionSettings {
  item: FunctionSettingsItem
  value: Record<string, FunctionItemDefaultValue>
  returnType?: ColumnTypes
}

export type IFunctionSettings = any

export interface IFunctionColumn extends ITableColumn {
  Settings: IFunctionSettings
}

export interface IFunctionCell extends ICell {
  settings: IFunctionSettings // settings of the type returned type of the cell to render
  returnType?: ColumnTypes
  toReturnTypeCell: () => ICell
}

/**
 * @todo create a proper settings for function based on fn defenition in makyo-common
 */
export class FunctionCell implements IFunctionCell {
  value: Cell | Cell[] = []
  settings: IFunctionSettings
  returnType: ColumnTypes | undefined = undefined
  timezone?: string

  constructor(value: Cell | Cell[], settings: IFunctionSettings, timezone?: string) {
    this.value = value
    this.settings = settings
    this.timezone = timezone

    const fnSettings = settings?.functionOptions?.functionValue as FunctionSettings | undefined
    if (fnSettings) {
      const fnItem = ALL_FUNCTIONS_MAP[fnSettings.item.id]
      if (fnItem) {
        this.returnType = fnItem.returnUnknown ? ColumnTypes.Text : (fnItem.returnType as ColumnTypes)
      }
    }
  }

  getColumnSettings(): Record<string, any> | null {
    switch (this.returnType) {
      case ColumnTypes.DateTime:
        return this.settings?.dtOptions
      case ColumnTypes.Number:
        return this.settings?.numberOptions
      default:
        return null
    }
  }

  toString(): string {
    if (!!this.returnType) {
      return this.toReturnTypeCell().toString()
    }

    if (Array.isArray(this.value)) {
      return this.value
        .map((v) => {
          if (v === undefined) {
            return ''
          }
          const rv: RowValue = v as RowValue
          return rv?.toString() || ''
        })
        .join(' | ')
    }

    if (this.value === undefined) {
      return ''
    }

    const rv: RowValue = this.value as RowValue
    return rv?.toString() || ''
  }

  toCell<T extends Cell>(newValue: T) {
    return this
  }

  /**
   * Transform the value into return type of the cell
   */
  toReturnTypeCell() {
    const value = this.value as Cell
    const colSettings = this.getColumnSettings()

    switch (this.returnType) {
      case ColumnTypes.Icon:
        return new IconCell(value, null)
      case ColumnTypes.Image:
        return new ImageCell(value, colSettings as any)
      case ColumnTypes.DateTime:
        return new DateTimeCell(value, colSettings as any, this.timezone)
      case ColumnTypes.Number:
        return new NumberCell(value, colSettings as any)
      case ColumnTypes.Boolean:
        return new BooleanCell(value, null)
      case ColumnTypes.Array:
        return new ArrayCell(value, null)
      default:
        return new TextCell(value, null)
    }
  }

  toMultipleDataCell<T extends Cell>(value: T[]): FunctionCell {
    this.value = value
    return this
  }
}
